import { ButtonHTMLAttributes, forwardRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  fullWidth?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      fullWidth = false,
      disabled,
      className = '',
      type = 'button',
      ...props
    },
    ref
  ) => {
    // reduced-motion 환경에서는 whileHover/whileTap 모션 자체를 비활성화 (docs/DESIGN_CUTE_ANIMATION.md 원칙 6)
    const prefersReducedMotion = useReducedMotion();
    const baseStyles =
      'inline-flex items-center justify-center font-semibold rounded-btn transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

    const variantStyles = {
      primary:
        'bg-[var(--accent)] text-[var(--bg)] hover:brightness-110 focus:ring-[var(--accent)] shadow-lg shadow-[var(--accent)]/30',
      secondary:
        'bg-[var(--surface)] text-[var(--text)] hover:brightness-125 focus:ring-[var(--border)] border border-[var(--border)]',
      ghost: 'bg-transparent text-[var(--accent)] border-2 border-[var(--accent)] hover:bg-[var(--accent)]/10 focus:ring-[var(--accent)]',
      danger: 'bg-[var(--danger)] text-[var(--bg)] hover:bg-[var(--danger-dark)] focus:ring-[var(--danger)] shadow-lg shadow-[var(--danger)]/50',
    };

    const sizeStyles = {
      sm: 'px-4 py-2 text-sm',
      md: 'px-6 py-3 text-base',
      lg: 'px-8 py-4 text-lg',
    };

    const widthStyles = fullWidth ? 'w-full' : '';

    return (
      <motion.button
        ref={ref}
        type={type}
        className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${widthStyles} ${className}`}
        disabled={disabled || isLoading}
        whileHover={prefersReducedMotion || disabled || isLoading ? undefined : { scale: 1.02 }}
        whileTap={prefersReducedMotion || disabled || isLoading ? undefined : { scale: 0.94 }}
        onClick={(e) => {
          if (props.onClick) props.onClick(e);
        }}
        {...props as any}
      >
        {isLoading ? (
          <>
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            처리 중...
          </>
        ) : (
          children
        )}
      </motion.button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
